import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Responsive, useContainerWidth } from "react-grid-layout";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";
import { api, fmt, fmtPct, dirArrow } from "@/lib/api";
import { useLiveTickers } from "@/lib/useLiveTickers";
import { useAuth } from "@/lib/auth";
import {
  Globe, Sparkles, TrendingUp, Calendar, Newspaper, PieChart,
  Bot, Gauge, Star, Zap, ArrowUpRight, ArrowDownRight,
} from "lucide-react";
import { toast } from "sonner";

const ResponsiveGridLayout = Responsive;

// WidthProvider shim (react-grid-layout v2 removed WidthProvider; use useContainerWidth hook)
function AutoWidthGrid({ children, layouts, onLayoutChange, ...rest }) {
  const { containerRef, width, mounted } = useContainerWidth({ initialWidth: 1200 });
  return (
    <div ref={containerRef} style={{ width: "100%" }}>
      {mounted && (
        <ResponsiveGridLayout
          {...rest}
          width={width}
          layouts={layouts}
          onLayoutChange={onLayoutChange}
        >
          {children}
        </ResponsiveGridLayout>
      )}
    </div>
  );
}

// Default widget layout — 12-col grid; user can drag/resize
const DEFAULT_LAYOUTS = {
  lg: [
    { i: "hero",       x: 0, y: 0, w: 12, h: 3, minH: 3, minW: 6, static: false },
    { i: "markets",    x: 0, y: 3, w: 8,  h: 8, minH: 5, minW: 4 },
    { i: "opportunity",x: 8, y: 3, w: 4,  h: 8, minH: 5, minW: 3 },
    { i: "trades",     x: 0, y: 11, w: 6, h: 7, minH: 5, minW: 3 },
    { i: "agents",     x: 6, y: 11, w: 3, h: 7, minH: 5, minW: 3 },
    { i: "risk",       x: 9, y: 11, w: 3, h: 7, minH: 5, minW: 3 },
    { i: "portfolio",  x: 0, y: 18, w: 4, h: 6, minH: 4, minW: 3 },
    { i: "watchlist",  x: 4, y: 18, w: 4, h: 6, minH: 4, minW: 3 },
    { i: "news",       x: 8, y: 18, w: 4, h: 6, minH: 4, minW: 3 },
    { i: "calendar",   x: 0, y: 24, w: 12, h: 5, minH: 4, minW: 6 },
  ],
  md: [
    { i: "hero",       x: 0, y: 0,  w: 10, h: 3 },
    { i: "markets",    x: 0, y: 3,  w: 10, h: 8 },
    { i: "opportunity",x: 0, y: 11, w: 5,  h: 6 },
    { i: "trades",     x: 5, y: 11, w: 5,  h: 6 },
    { i: "agents",     x: 0, y: 17, w: 5,  h: 5 },
    { i: "risk",       x: 5, y: 17, w: 5,  h: 5 },
    { i: "portfolio",  x: 0, y: 22, w: 5,  h: 5 },
    { i: "watchlist",  x: 5, y: 22, w: 5,  h: 5 },
    { i: "news",       x: 0, y: 27, w: 10, h: 5 },
    { i: "calendar",   x: 0, y: 32, w: 10, h: 5 },
  ],
};

const STORE_KEY = "sth.dashboard.layouts.v1";

export default function Dashboard() {
  const nav = useNavigate();
  const { user } = useAuth();
  const { tickers: liveTickers } = useLiveTickers();
  const [sectors, setSectors] = useState([]);
  const [movers, setMovers] = useState({ gainers: [], losers: [] });
  const [loading, setLoading] = useState(true);
  const [command, setCommand] = useState("");
  const [layouts, setLayouts] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORE_KEY)) || DEFAULT_LAYOUTS; } catch { return DEFAULT_LAYOUTS; }
  });

  useEffect(() => {
    const load = async () => {
      try {
        const [hm, mv] = await Promise.all([api.get("/market/heatmap"), api.get("/market/movers")]);
        setSectors(hm.data.sectors || []);
        setMovers(mv.data || { gainers: [], losers: [] });
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    load();
    const iv = setInterval(load, 60000);
    return () => clearInterval(iv);
  }, []);

  const tickerArr = useMemo(() => Object.values(liveTickers || {}), [liveTickers]);
  const grouped = useMemo(() => ({
    IN: tickerArr.filter(t => t.category === "IN"),
    US: tickerArr.filter(t => t.category === "US"),
    CRYPTO: tickerArr.filter(t => t.category === "CRYPTO"),
    COMMODITY: tickerArr.filter(t => t.category === "COMMODITY"),
    FX: tickerArr.filter(t => t.category === "FX"),
  }), [tickerArr]);

  const runCommand = (e) => {
    e?.preventDefault();
    const q = command.trim();
    if (!q) return;
    if (q.startsWith("/")) {
      const [cmd, ...args] = q.slice(1).split(" ");
      if (cmd === "trade" && args[0]) nav(`/trade-ideas?s=${encodeURIComponent(args[0].toUpperCase())}`);
      else if (cmd === "chart" && args[0]) nav(`/chart?s=${encodeURIComponent(args[0].toUpperCase())}`);
      else if (cmd === "chat") nav(`/chat`);
      else toast.error("Unknown command. Try /trade AAPL or /chart NVDA");
    } else {
      // Interpret as symbol
      nav(`/trade-ideas?s=${encodeURIComponent(q.toUpperCase())}`);
    }
    setCommand("");
  };

  const onLayoutChange = (_, all) => {
    setLayouts(all);
    localStorage.setItem(STORE_KEY, JSON.stringify(all));
  };

  const resetLayout = () => {
    setLayouts(DEFAULT_LAYOUTS);
    localStorage.removeItem(STORE_KEY);
    toast.success("Layout reset");
  };

  return (
    <div className="px-4 md:px-6 lg:px-8 py-6 animate-fade-up" data-testid="dashboard-page">
      {/* PAGE HEADER */}
      <div className="mb-6 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="section-eyebrow mb-2">◆ Mission Control</div>
          <h1 className="text-5xl md:text-6xl font-serif-elite text-[color:var(--text-primary)]">
            <span className="gradient-text-purple">Command Center</span>
          </h1>
          <p className="text-[color:var(--text-secondary)] mt-2 text-base md:text-lg max-w-2xl">
            Real-time institutional intelligence · 30-agent AI mesh · Continuous market synthesis
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={resetLayout} className="btn btn-ghost text-sm" data-testid="reset-layout-btn" title="Reset widget layout">
            Reset layout
          </button>
          <Link to="/research" className="btn btn-ghost" data-testid="cta-research">
            Run AI Research
          </Link>
          <Link to="/trade-ideas" className="btn btn-primary" data-testid="cta-trade">
            <Sparkles size={16} /> Generate Trade
          </Link>
        </div>
      </div>

      <AutoWidthGrid
        className="layout"
        layouts={layouts}
        breakpoints={{ lg: 1200, md: 900, sm: 600, xs: 0 }}
        cols={{ lg: 12, md: 10, sm: 6, xs: 4 }}
        rowHeight={44}
        margin={[16, 16]}
        containerPadding={[0, 0]}
        onLayoutChange={onLayoutChange}
        draggableHandle=".widget-drag-handle"
        useCSSTransforms
      >
        <div key="hero"><HeroCommandBar command={command} setCommand={setCommand} onSubmit={runCommand} /></div>
        <div key="markets"><WidgetGlobalMarkets grouped={grouped} loading={loading && tickerArr.length === 0} /></div>
        <div key="opportunity"><WidgetOpportunity movers={movers} /></div>
        <div key="trades"><WidgetTopTradeIdeas /></div>
        <div key="agents"><WidgetAgentStatus /></div>
        <div key="risk"><WidgetRiskMeter sectors={sectors} /></div>
        <div key="portfolio"><WidgetPortfolioSnapshot user={user} /></div>
        <div key="watchlist"><WidgetWatchlist tickers={tickerArr} /></div>
        <div key="news"><WidgetBreakingNews /></div>
        <div key="calendar"><WidgetEconomicCalendar /></div>
      </AutoWidthGrid>
    </div>
  );
}

/* --------------------------- Widget Shell --------------------------- */

function Widget({ title, icon: Icon, accent = "purple", subtitle, children, action, tid }) {
  const accentColor = { purple: "var(--ai)", emerald: "var(--bull)", gold: "var(--gold)", blue: "var(--info)", bear: "var(--bear)" }[accent] || "var(--ai)";
  return (
    <div className="h-full card p-4 md:p-5 flex flex-col overflow-hidden" data-testid={tid}>
      <div className="widget-drag-handle -mx-4 md:-mx-5 -mt-4 md:-mt-5 px-4 md:px-5 pt-4 pb-3 flex items-center gap-2.5 cursor-grab active:cursor-grabbing select-none">
        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${accentColor}18`, color: accentColor }}>
          <Icon size={16} strokeWidth={2.4} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold text-[color:var(--text-primary)] truncate">{title}</div>
          {subtitle && <div className="text-[11px] text-[color:var(--text-tertiary)] truncate">{subtitle}</div>}
        </div>
        {action && <div className="shrink-0">{action}</div>}
      </div>
      <div className="border-t border-[color:var(--border-subtle)] -mx-4 md:-mx-5 mb-3" />
      <div className="flex-1 min-h-0 overflow-y-auto -mr-1 pr-1">
        {children}
      </div>
    </div>
  );
}

/* --------------------------- Widgets --------------------------- */

function HeroCommandBar({ command, setCommand, onSubmit }) {
  return (
    <div className="h-full card-elev overflow-hidden relative" data-testid="hero-widget" style={{ background: "var(--grad-hero)" }}>
      <div className="absolute inset-0 opacity-40" style={{ background: "var(--grad-mesh)" }} />
      <div className="relative h-full p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
        <div className="widget-drag-handle absolute top-2 right-2 text-[10px] font-mono-data text-[color:var(--text-muted)] cursor-grab active:cursor-grabbing px-2 py-1 select-none">::</div>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--grad-purple)", boxShadow: "var(--shadow-glow-purple)" }}>
            <Zap size={22} color="white" strokeWidth={2.4} />
          </div>
          <div>
            <div className="text-[11px] font-mono-data tracking-widest text-[color:var(--gold)] font-semibold">◆ AI COMMAND BAR</div>
            <div className="text-lg md:text-xl font-semibold text-[color:var(--text-primary)]">Ask, trade, or analyze in one line.</div>
          </div>
        </div>
        <form onSubmit={onSubmit} className="flex-1 w-full flex items-center gap-2 min-w-0">
          <input
            data-testid="hero-command-input"
            value={command}
            onChange={(e) => setCommand(e.target.value)}
            placeholder="e.g. NVDA · /trade AAPL · /chart BTC-USD · /chat"
            className="input !py-3 flex-1 text-base"
          />
          <button type="submit" className="btn btn-primary whitespace-nowrap" data-testid="hero-command-submit">
            Execute <ArrowUpRight size={16} />
          </button>
        </form>
      </div>
    </div>
  );
}

function WidgetGlobalMarkets({ grouped, loading }) {
  return (
    <Widget title="Global Markets" subtitle="Cross-asset live snapshot" icon={Globe} accent="blue" tid="widget-markets" action={<Link to="/chart" className="text-xs text-[color:var(--info)] hover:underline">View all →</Link>}>
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Array.from({ length: 9 }).map((_, i) => <div key={i} className="skeleton h-20 rounded-lg" />)}
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {Object.entries(grouped).flatMap(([cat, arr]) =>
            arr.slice(0, 3).map(t => (
              <Link
                to={`/trade-ideas?s=${encodeURIComponent(t.symbol)}`}
                key={t.symbol}
                className="p-3 rounded-lg border border-[color:var(--border-subtle)] hover:border-[color:var(--border-focus)] bg-[color:var(--bg-elevated)] transition-colors block group"
                data-testid={`quote-card-${t.symbol}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="chip !py-0.5 !px-1.5 text-[9px] uppercase tracking-widest">{cat}</span>
                  <span className={`text-xs ${t.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                    {t.change_pct >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                  </span>
                </div>
                <div className="text-[11px] text-[color:var(--text-tertiary)] mb-1 truncate">{t.name}</div>
                <div className="font-mono-data text-base font-semibold text-[color:var(--text-primary)]">{fmt(t.price)}</div>
                <div className={`font-mono-data text-xs ${t.change_pct >= 0 ? "text-bull" : "text-bear"}`}>
                  {fmtPct(t.change_pct)}
                </div>
              </Link>
            ))
          )}
        </div>
      )}
    </Widget>
  );
}

function WidgetOpportunity({ movers }) {
  const opps = [
    ...(movers.gainers || []).slice(0, 3).map(m => ({ ...m, kind: "MOMENTUM" })),
    ...(movers.losers  || []).slice(0, 2).map(m => ({ ...m, kind: "MEAN-REV" })),
  ];
  return (
    <Widget title="AI Opportunity Scanner" subtitle="Momentum · Mean-reversion" icon={Sparkles} accent="gold" tid="widget-opportunity" action={<Link to="/trade-ideas" className="text-xs text-[color:var(--gold)] hover:underline">Generate →</Link>}>
      {opps.length === 0 ? (
        <div className="text-sm text-[color:var(--text-tertiary)]">Scanning market for setups…</div>
      ) : (
        <div className="space-y-2">
          {opps.map((o) => (
            <Link key={o.symbol + o.kind} to={`/trade-ideas?s=${encodeURIComponent(o.symbol)}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg hover:bg-[color:var(--bg-elevated)] transition-colors" data-testid={`opp-${o.symbol}`}>
              <div className="flex items-center gap-2 min-w-0">
                <span className={`chip !text-[9px] !py-0.5 !px-1.5 ${o.kind === "MOMENTUM" ? "chip-gold" : "chip-info"}`}>{o.kind}</span>
                <span className="font-mono-data text-sm font-semibold truncate">{o.symbol}</span>
              </div>
              <span className={`font-mono-data text-sm ${o.change_pct >= 0 ? "text-bull" : "text-bear"}`}>{fmtPct(o.change_pct)}</span>
            </Link>
          ))}
        </div>
      )}
    </Widget>
  );
}

function WidgetTopTradeIdeas() {
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDemo, setIsDemo] = useState(false);
  useEffect(() => {
    // Optimistic: fetch generated trade ideas if endpoint exists; else show curated fallback
    api.get("/ai/trade-ideas/recent").then(({ data }) => {
      setIdeas(data.items || []);
      setIsDemo(!(data.items && data.items.length));
    }).catch(() => { setIdeas([]); setIsDemo(true); }).finally(() => setLoading(false));
  }, []);
  const showcase = ideas.length ? ideas.slice(0, 4) : [
    { symbol: "NVDA", direction: "LONG", conviction: "HIGH", strategy_name: "AI momentum swing", win_probability_pct: 68 },
    { symbol: "BTC-USD", direction: "LONG", conviction: "MODERATE", strategy_name: "Breakout continuation", win_probability_pct: 62 },
    { symbol: "GLD", direction: "LONG", conviction: "MODERATE", strategy_name: "Macro hedge", win_probability_pct: 58 },
    { symbol: "TSLA", direction: "SHORT", conviction: "LOW", strategy_name: "Mean reversion", win_probability_pct: 54 },
  ];
  return (
    <Widget title="Top Trade Ideas" subtitle="Kelly-sized institutional setups" icon={TrendingUp} accent="emerald" tid="widget-trades" action={
      <div className="flex items-center gap-1.5">
        {isDemo && <span className="chip !text-[9px] !py-0 !px-1.5" data-testid="trades-demo-chip">Demo</span>}
        <Link to="/trade-ideas" className="text-xs text-[color:var(--bull)] hover:underline">All ideas →</Link>
      </div>
    }>
      {loading ? (
        <div className="space-y-2">{Array.from({length:4}).map((_,i)=><div key={i} className="skeleton h-14"/>)}</div>
      ) : (
        <div className="space-y-2">
          {showcase.map((t, i) => (
            <Link to={`/trade-ideas?s=${encodeURIComponent(t.symbol)}`} key={i} className="flex items-center gap-3 p-3 rounded-lg hover:bg-[color:var(--bg-elevated)] transition-colors border border-[color:var(--border-subtle)]" data-testid={`trade-idea-${t.symbol}`}>
              <div className={`w-9 h-9 rounded-md flex items-center justify-center shrink-0 font-mono-data text-xs font-bold ${t.direction === "LONG" ? "bg-[color:var(--bull)]/15 text-[color:var(--bull)]" : "bg-[color:var(--bear)]/15 text-[color:var(--bear)]"}`}>
                {t.direction === "LONG" ? "L" : "S"}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono-data font-semibold text-sm text-[color:var(--text-primary)]">{t.symbol}</span>
                  <span className="chip chip-gold !text-[9px] !py-0 !px-1.5">{t.conviction}</span>
                </div>
                <div className="text-xs text-[color:var(--text-tertiary)] truncate">{t.strategy_name}</div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-xs text-[color:var(--text-muted)]">Win</div>
                <div className="font-mono-data font-semibold text-sm text-[color:var(--text-primary)]">{t.win_probability_pct}%</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Widget>
  );
}

function WidgetAgentStatus() {
  const [agents, setAgents] = useState([]);
  useEffect(() => {
    api.get("/agents").then(({ data }) => setAgents(data.agents || [])).catch(() => {});
  }, []);
  const shown = agents.slice(0, 6);
  return (
    <Widget title="AI Agent Mesh" subtitle={`${agents.length || 30} specialists online`} icon={Bot} accent="purple" tid="widget-agents" action={<Link to="/agents" className="text-xs text-[color:var(--ai)] hover:underline">All →</Link>}>
      <div className="space-y-2">
        {shown.length === 0
          ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="skeleton h-8" />)
          : shown.map(a => (
              <Link to={`/agents/${a.id}`} key={a.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-[color:var(--bg-elevated)] transition-colors" data-testid={`agent-${a.id}`}>
                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: a.color || "var(--ai)" }} />
                <span className="text-sm text-[color:var(--text-primary)] truncate flex-1">{a.name}</span>
                <span className="dot-pulse !w-1.5 !h-1.5" />
              </Link>
            ))
        }
      </div>
    </Widget>
  );
}

function WidgetRiskMeter({ sectors }) {
  // Aggregate volatility proxy from sector data
  const meter = useMemo(() => {
    if (!sectors.length) return { level: 5, label: "MODERATE", vol: 0 };
    const vol = sectors.reduce((s, x) => s + Math.abs(x.change_pct || 0), 0) / sectors.length;
    const level = Math.min(10, Math.max(1, Math.round(vol * 3.5)));
    const label = level >= 8 ? "EXTREME" : level >= 6 ? "ELEVATED" : level >= 4 ? "MODERATE" : "CALM";
    return { level, label, vol };
  }, [sectors]);
  const color = meter.level >= 7 ? "var(--bear)" : meter.level >= 4 ? "var(--gold)" : "var(--bull)";
  return (
    <Widget title="Risk Meter" subtitle="Aggregated market volatility" icon={Gauge} accent={meter.level >= 7 ? "bear" : meter.level >= 4 ? "gold" : "emerald"} tid="widget-risk">
      <div className="flex flex-col items-center justify-center h-full py-2">
        <div className="relative w-36 h-36">
          <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
            <circle cx="50" cy="50" r="42" stroke="var(--bg-elevated)" strokeWidth="10" fill="none" />
            <circle cx="50" cy="50" r="42" stroke={color} strokeWidth="10" fill="none"
              strokeLinecap="round"
              strokeDasharray={`${(meter.level / 10) * 264} 264`} />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <div className="text-3xl font-bold" style={{ color }}>{meter.level}</div>
            <div className="text-[10px] font-mono-data tracking-widest" style={{ color }}>{meter.label}</div>
          </div>
        </div>
        <div className="text-xs text-[color:var(--text-tertiary)] mt-3 text-center">
          Avg sector move: <span className="font-mono-data text-[color:var(--text-primary)]">{meter.vol.toFixed(2)}%</span>
        </div>
      </div>
    </Widget>
  );
}

function WidgetPortfolioSnapshot({ user }) {
  return (
    <Widget title="Portfolio Snapshot" subtitle="Demo data · connect broker to sync" icon={PieChart} accent="emerald" tid="widget-portfolio" action={
      <div className="flex items-center gap-1.5">
        <span className="chip !text-[9px] !py-0 !px-1.5" data-testid="portfolio-demo-chip">Demo</span>
        <Link to="/brokers" className="text-xs text-[color:var(--bull)] hover:underline">{user ? "Sync →" : "Connect →"}</Link>
      </div>
    }>
      <div className="space-y-3">
        <div className="flex items-baseline justify-between">
          <div>
            <div className="text-xs text-[color:var(--text-tertiary)]">Total Value</div>
            <div className="text-2xl font-bold font-mono-data text-[color:var(--text-primary)]">$100,000</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[color:var(--text-tertiary)]">P&L Today</div>
            <div className="text-lg font-mono-data text-bull">+$1,240</div>
          </div>
        </div>
        <div className="space-y-1.5">
          {[
            { name: "Equities", pct: 55, color: "var(--info)" },
            { name: "Crypto",   pct: 20, color: "var(--gold)" },
            { name: "Bonds",    pct: 15, color: "var(--bull)" },
            { name: "Cash",     pct: 10, color: "var(--neutral)" },
          ].map(a => (
            <div key={a.name}>
              <div className="flex justify-between text-xs mb-1">
                <span className="text-[color:var(--text-secondary)]">{a.name}</span>
                <span className="font-mono-data text-[color:var(--text-primary)]">{a.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-[color:var(--bg-elevated)] overflow-hidden">
                <div className="h-full rounded-full" style={{ width: `${a.pct}%`, background: a.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </Widget>
  );
}

function WidgetWatchlist({ tickers }) {
  const list = tickers.slice(0, 6);
  return (
    <Widget title="Watchlist" subtitle="Live prices" icon={Star} accent="gold" tid="widget-watchlist">
      {list.length === 0 ? (
        <div className="space-y-2">{Array.from({length:5}).map((_,i)=><div key={i} className="skeleton h-8" />)}</div>
      ) : (
        <div className="space-y-1">
          {list.map(t => (
            <Link to={`/chart?s=${encodeURIComponent(t.symbol)}`} key={t.symbol} className="flex items-center justify-between px-2 py-2 rounded hover:bg-[color:var(--bg-elevated)] transition-colors" data-testid={`watch-${t.symbol}`}>
              <div className="min-w-0">
                <div className="text-sm font-mono-data font-semibold truncate">{t.symbol}</div>
                <div className="text-[10px] text-[color:var(--text-tertiary)] truncate">{t.name}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-mono-data text-[color:var(--text-primary)]">{fmt(t.price)}</div>
                <div className={`text-xs font-mono-data ${t.change_pct >= 0 ? "text-bull" : "text-bear"}`}>{fmtPct(t.change_pct)}</div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </Widget>
  );
}

function WidgetBreakingNews() {
  const news = [
    { time: "12 min", tag: "MACRO",   title: "Fed minutes signal patient stance; markets rally into close." },
    { time: "34 min", tag: "TECH",    title: "NVDA extends AI-chip lead; sell-side upgrades price targets." },
    { time: "1 h",    tag: "CRYPTO",  title: "BTC breaks $100k psychological level on institutional inflows." },
    { time: "2 h",    tag: "EARN",    title: "Q4 mega-cap earnings preview: expectations vs positioning." },
  ];
  return (
    <Widget title="Breaking News" subtitle="Curated · AI summaries coming soon" icon={Newspaper} accent="blue" tid="widget-news" action={<span className="chip !text-[9px] !py-0 !px-1.5" data-testid="news-demo-chip">Demo</span>}>
      <div className="space-y-2.5">
        {news.map((n, i) => (
          <div key={i} className="p-2.5 rounded-lg border border-[color:var(--border-subtle)] hover:border-[color:var(--border-focus)] transition-colors">
            <div className="flex items-center gap-2 mb-1">
              <span className="chip chip-info !text-[9px] !py-0.5 !px-1.5">{n.tag}</span>
              <span className="text-[10px] text-[color:var(--text-muted)] font-mono-data">{n.time}</span>
            </div>
            <div className="text-sm text-[color:var(--text-primary)] leading-snug">{n.title}</div>
          </div>
        ))}
      </div>
    </Widget>
  );
}

function WidgetEconomicCalendar() {
  const events = [
    { time: "08:30 ET", ccy: "US", event: "CPI (YoY)",           impact: "HIGH",   forecast: "3.2%", prev: "3.4%" },
    { time: "10:00 ET", ccy: "US", event: "Fed Chair Speaks",     impact: "HIGH",   forecast: "—",   prev: "—" },
    { time: "13:00 ET", ccy: "US", event: "10-Yr Treasury Auction",impact: "MED",   forecast: "—",   prev: "4.25%" },
    { time: "22:00 IST",ccy: "IN", event: "RBI Rate Decision",     impact: "HIGH",  forecast: "6.5%",prev: "6.5%" },
    { time: "02:00 UTC",ccy: "EU", event: "ECB Minutes",           impact: "MED",   forecast: "—",   prev: "—" },
  ];
  return (
    <Widget title="Economic Calendar" subtitle="Curated · Next 24 hours" icon={Calendar} accent="purple" tid="widget-calendar" action={<span className="chip !text-[9px] !py-0 !px-1.5" data-testid="calendar-demo-chip">Demo</span>}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[10px] font-mono-data tracking-widest text-[color:var(--text-muted)]">
              <th className="px-2 py-2">TIME</th><th className="px-2 py-2">CCY</th><th className="px-2 py-2">EVENT</th>
              <th className="px-2 py-2">IMPACT</th><th className="px-2 py-2 text-right">FORECAST</th><th className="px-2 py-2 text-right">PREV</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e, i) => (
              <tr key={i} className="border-t border-[color:var(--border-subtle)] hover:bg-[color:var(--bg-elevated)]/60">
                <td className="px-2 py-2.5 font-mono-data text-[color:var(--text-tertiary)]">{e.time}</td>
                <td className="px-2 py-2.5 font-mono-data font-semibold">{e.ccy}</td>
                <td className="px-2 py-2.5 text-[color:var(--text-primary)]">{e.event}</td>
                <td className="px-2 py-2.5">
                  <span className={`chip !text-[9px] !py-0.5 !px-1.5 ${e.impact === "HIGH" ? "chip-bear" : "chip-gold"}`}>{e.impact}</span>
                </td>
                <td className="px-2 py-2.5 text-right font-mono-data">{e.forecast}</td>
                <td className="px-2 py-2.5 text-right font-mono-data text-[color:var(--text-tertiary)]">{e.prev}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Widget>
  );
}
